import { useCallback, useEffect, useRef, useState } from 'react';

import { logService } from '../services';

interface MediaDevice {
  deviceId: string;
  label: string;
  kind: MediaDeviceKind;
}

const useMediaDevices = () => {
  const hasRequestedPermission = useRef(false);
  const [microphoneDevices, setMicrophoneDevices] = useState<MediaDevice[]>([]);
  const [speakerDevices, setSpeakerDevices] = useState<MediaDevice[]>([]);

  const requestPermission = useCallback(async() => {
    try {
      // Check if mediaDevices API is available
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        const message = 'Media devices API is not available in this browser/context';
        logService.addLog(message);
        console.warn(message);
        return;
      }

      const permissions = await navigator.permissions.query({
        name: 'microphone' as PermissionName,
      });

      if (permissions.state !== 'granted') {
        await navigator.mediaDevices.getUserMedia({ audio: true });
        const message = 'Audio permissions granted successfully!';
        logService.addLog(message);
      }

      // After permissions are granted, enumerate devices
      const devices = await navigator.mediaDevices.enumerateDevices();

      const inputDevices = devices
        .filter(device => device.kind === 'audioinput')
        .map(device => ({
          deviceId: device.deviceId,
          label: device.label || `Microphone ${device.deviceId.slice(0, 5)}...`,
          kind: device.kind,
        }));

      const outputDevices = devices
        .filter(device => device.kind === 'audiooutput')
        .map(device => ({
          deviceId: device.deviceId,
          label: device.label || `Speaker ${device.deviceId.slice(0, 5)}...`,
          kind: device.kind,
        }));

      setMicrophoneDevices(inputDevices);
      setSpeakerDevices(outputDevices);
    } catch (error) {
      const message = `Error accessing audio devices:\n ${error}`;
      logService.addLog(message);
      console.error(message);
    }
  }, []);

  useEffect(() => {
    if (hasRequestedPermission.current) return; // Prevent double execution

    hasRequestedPermission.current = true;
    requestPermission();

    // Check if mediaDevices API is available before adding listeners
    if (!navigator.mediaDevices) {
      return;
    }

    const handleDeviceChange = () => {
      requestPermission();
    };

    navigator.mediaDevices.addEventListener('devicechange', handleDeviceChange);

    return () => {
      navigator.mediaDevices.removeEventListener(
        'devicechange',
        handleDeviceChange,
      );
    };
  }, [requestPermission]);

  return { requestPermission, microphoneDevices, speakerDevices };
};

export default useMediaDevices;
